import React, { ComponentProps } from 'react';
import { asView } from '../../../_lib/ViewFetcher';
import { PostFirst, PostsList, PostWithReplies } from '../../../_lib/components/Posts';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ThreadAttachmentListDefault } from '../partials/ThreadAttachmentListDefault';
import { ThreadBlockMainEditor } from '../partials/ThreadBlockMainEditor';
import { useWebsocketSyncedErrors } from '../useWebsocketSyncedErrors';
import { ThreadPostAttachmentsError } from '../partials/ThreadPostAttachmentsError';
import { SkipLink } from '../../../_lib/components/SkipLink';
import { SonForm } from '../../../_lib/Form2';
import { HeadingHierarchy } from '../../../_lib/components/Heading/Heading';

type ThreadDefaultProps = { createForm?: SonForm<any>; posts: PostFirst[]; cleanupOldPostsAfterDays?: number };

function ThreadDefault({ createForm, posts, cleanupOldPostsAfterDays }: ThreadDefaultProps) {
  const { t } = useSonTranslation('Posts');

  const { getErrorsByPostId, resetErrors } = useWebsocketSyncedErrors();

  const getThreadPostAttachmentsError = (postId: string) => {
    const errorsForPostId = getErrorsByPostId(postId);

    return errorsForPostId.length === 0 ? null : (
      <ThreadPostAttachmentsError postId={postId} errors={getErrorsByPostId(postId)} resetErrors={resetErrors} />
    );
  };

  /**
   * Heading hierarchies
   */
  const headingHierarchy: HeadingHierarchy = 2;
  const subHeadingsHierarchy: HeadingHierarchy = (headingHierarchy + 1) as HeadingHierarchy;
  const postHeadingsHierarchy: HeadingHierarchy = (subHeadingsHierarchy + 1) as HeadingHierarchy;

  /**
   * different default view in edit mode to inform user of activation
   * no information if disabled
   * */
  return (
    <div className="posts">
      {posts && posts.length > 0 && (
        <>
          <SkipLink id="threads-main-editor">{t('default.threadSkipLinkText')}</SkipLink>
          <PostsList headingHierarchy={subHeadingsHierarchy} type="thread" cleanUpPeriod={cleanupOldPostsAfterDays}>
            {posts.map((post, index) => (
              <PostWithReplies
                key={post.id}
                post={post}
                index={index}
                headingHierarchy={postHeadingsHierarchy}
                previousPostId={index > 0 ? posts[index - 1].id : undefined}
                type="thread"
              >
                {getThreadPostAttachmentsError(post.id)}
                {post?.attachments && <ThreadAttachmentListDefault attachments={post.attachments} />}
              </PostWithReplies>
            ))}
          </PostsList>
        </>
      )}
      <ThreadBlockMainEditor
        hierarchy={subHeadingsHierarchy}
        createForm={createForm}
        headline={t('default.editor.headline.thread')}
      />
    </div>
  );
}

export const ThreadDefaultView = asView(ThreadDefault);
export type ThreadDefaultView = ComponentProps<typeof ThreadDefaultView>;
